// Add, edit or delete one row of a HOSTED project database through a form generated from the
// hosted design (backend operate/row_schema.py, routers/data_rows.py):
//
//   <RowForm projectId table rowKey={null | key} onDone={() => ...} onCancel={() => ...} />
//
// GET  .../data/tables/{table}/form          JSON Schema + uiSchema (RJSF on the BOKU theme)
// GET  .../data/tables/{table}/rows/{key}    the row and its `version` (edit)
// POST .../rows {values} | PATCH .../rows/{key} {values, version} | DELETE .../rows/{key}?version=
//
// Widgets beyond the theme: "foreignKey" (the referenced table's rows in a combobox, the chosen
// row as a card; ForeignKeyWidget.jsx), "mintedKey" (the key with a "Mint identifier" button), "fileUpload" (a
// file column: the file is uploaded first, the row then sends {"upload": id}; FileUploadWidget.jsx,
// liveFiles.js). A 422 is shown
// on its fields; a 409 says why (another editor changed the row: "Use the current values").
import { useEffect, useMemo, useRef, useState } from "react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Btn, Card, Input, Spinner } from "../../components/Shared";
import { fields, templates, widgets } from "../../forms/theme";
import FileUploadWidget from "./FileUploadWidget";
import ForeignKeyWidget from "./ForeignKeyWidget";
import { withUploads } from "./liveFiles";
import { rowErrorList, rowErrors, toFormData, toValues } from "./rowFormUtils";

const alertStyle = { padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)",
                     border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 };
const noteStyle = { fontSize: 12, color: "#4b5563", marginTop: 4 };

// The primary key, with the table's identifier minter offered (operate/minting.py).
function MintedKeyWidget({ id, value, onChange, options, disabled, readonly, required, registry, label }) {
  const mint = options.mint || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const draw = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/projects/${registry.formContext.projectId}/minting/${encodeURIComponent(mint.target)}/mint`,
                               { count: 1 });
      onChange(r.identifiers[0]);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Input id={id} value={value == null ? "" : value} disabled={disabled} readOnly={readonly}
               required={required} aria-required={required || undefined} aria-label={label}
               onChange={e => onChange(e.target.value === "" ? undefined : e.target.value)} />
        {!(disabled || readonly) && (
          <Btn variant="secondary" onClick={draw} disabled={busy}>
            {busy && <Spinner />} Mint identifier
          </Btn>
        )}
      </div>
      <div style={noteStyle}>Next from the minter: <code>{mint.next}</code> (format <code>{mint.format}</code>)</div>
      {error && <div role="alert" style={{ ...noteStyle, color: "#b91c1c" }}>{error.message}</div>}
    </div>
  );
}

const ROW_WIDGETS = { ...widgets, foreignKey: ForeignKeyWidget, mintedKey: MintedKeyWidget,
                      fileUpload: FileUploadWidget };

function withoutSubmit(ui) {
  const out = { ...(ui || {}) };
  delete out["ui:submitButtonOptions"];
  return out;
}

export default function RowForm({ projectId, table, rowKey = null, onDone, onCancel }) {
  const base = `/projects/${projectId}/data/tables/${encodeURIComponent(table)}`;
  const editing = rowKey !== null && rowKey !== undefined;
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [initial, setInitial] = useState(null);
  const [version, setVersion] = useState(null);
  const [formData, setFormData] = useState({});
  const [extraErrors, setExtraErrors] = useState(undefined);
  const [problems, setProblems] = useState([]);
  const [conflict, setConflict] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [status, setStatus] = useState("");
  const uploads = useRef({});            // {column: the uploaded object} of this form

  useEffect(() => {
    let live = true;
    setLoadError(null);
    const loads = [api.get(`${base}/form`)];
    if (editing) loads.push(api.get(`${base}/rows/${encodeURIComponent(rowKey)}`));
    Promise.all(loads)
      .then(([f, r]) => {
        if (!live) return;
        setForm(f);
        const data = editing ? toFormData(f, r.row) : {};
        setInitial(editing ? data : null);
        setFormData(data);
        setVersion(editing ? r.version : null);
      })
      .catch(err => { if (live) setLoadError(err); });
    return () => { live = false; };
  }, [base, editing, rowKey]);

  const schema = useMemo(() => (form ? form.schema : null), [form]);
  const uiSchema = useMemo(() => (form ? withoutSubmit(form.uiSchema) : {}), [form]);
  const context = useMemo(() => ({
    projectId, table, onUpload: (column, up) => { uploads.current = { ...uploads.current, [column]: up }; },
  }), [projectId, table]);

  const fail = err => {
    if (err.status === 422 && Array.isArray(err.detail)) {
      setExtraErrors(rowErrors(err.detail).schema);
      setProblems(rowErrorList(err.detail));
    } else if (err.status === 409 && err.detail && err.detail.current) {
      setConflict({ message: err.detail.message, current: err.detail.current });
    } else {
      setError(err);
    }
    setStatus("Not saved.");
  };

  const reset = () => {
    setExtraErrors(undefined);
    setProblems([]);
    setConflict(null);
    setError(null);
  };

  const submit = async ({ formData: data }) => {
    reset();
    const values = withUploads(toValues(form, data, initial), data, uploads.current);
    if (editing && Object.keys(values).length === 0) {
      setStatus("Nothing changed.");
      return;
    }
    setBusy(true);
    try {
      const saved = editing
        ? await api.patch(`${base}/rows/${encodeURIComponent(rowKey)}`, { values, version })
        : await api.post(`${base}/rows`, { values });
      setStatus("Saved.");
      onDone && onDone(saved, editing ? "updated" : "created");
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const useCurrent = () => {
    const data = toFormData(form, conflict.current.row);
    setInitial(data);
    setFormData(data);
    setVersion(conflict.current.version);
    setConflict(null);
    setStatus("The current values are loaded; make your change again.");
  };

  const remove = async () => {
    reset();
    setBusy(true);
    try {
      await api.del(`${base}/rows/${encodeURIComponent(rowKey)}?version=${encodeURIComponent(version)}`);
      onDone && onDone(null, "deleted");
    } catch (err) {
      setConfirmDelete(false);
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const title = editing ? `Edit row ${rowKey} of ${table}` : `Add a row to ${table}`;
  return (
    <Card style={{ marginBottom: 16 }}>
      <section aria-label={title}>
        <h2 style={{ margin: "0 0 12px", fontSize: 18 }}>{title}</h2>
        <ErrorNote error={loadError} />
        {!form && !loadError && <Loading label="Loading the form…" />}
        {problems.length > 0 && (
          <div role="alert" style={{ ...alertStyle, marginBottom: 12 }}>
            <p style={{ margin: "0 0 6px", fontWeight: 700 }}>The database did not accept the row:</p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
          </div>
        )}
        {conflict && (
          <div role="alert" style={{ ...alertStyle, marginBottom: 12 }}>
            <p style={{ margin: "0 0 8px" }}>{conflict.message}</p>
            <Btn variant="secondary" onClick={useCurrent}>Use the current values</Btn>
          </div>
        )}
        <ErrorNote error={error} />
        {form && (
          <Form
            aria-label={title}
            schema={schema}
            uiSchema={uiSchema}
            formData={formData}
            formContext={context}
            validator={validator}
            widgets={ROW_WIDGETS}
            fields={fields}
            templates={templates}
            extraErrors={extraErrors}
            showErrorList="top"
            focusOnFirstError
            noHtml5Validate
            onChange={e => setFormData(e.formData)}
            onSubmit={submit}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 20, paddingTop: 16,
                          borderTop: "1px solid #e5e7eb", flexWrap: "wrap" }}>
              <Btn type="submit" disabled={busy}>{busy && <Spinner />} {editing ? "Save row" : "Add row"}</Btn>
              <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
              {editing && form.primary_key && !confirmDelete && (
                <Btn variant="danger" onClick={() => setConfirmDelete(true)} disabled={busy}>Delete row</Btn>
              )}
              {confirmDelete && (
                <span role="group" aria-label="Confirm delete" style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 13 }}>Delete row <code>{String(rowKey)}</code> for good?</span>
                  <Btn variant="danger" onClick={remove} disabled={busy}>Yes, delete</Btn>
                  <Btn variant="secondary" onClick={() => setConfirmDelete(false)}>Keep it</Btn>
                </span>
              )}
              <span role="status" style={{ fontSize: 13, color: "#6b7280" }}>{status}</span>
            </div>
          </Form>
        )}
      </section>
    </Card>
  );
}
